import { useState, useRef, useCallback } from 'react';
import { SK_WIDGET_ORDER, SK_WIDGET_VISIBLE } from '../constants/storageKeys';

export const DEFAULT_WIDGET_ORDER = [
  'checkin', 'weather', 'journeys', 'medications', 'quick-access',
  'patterns', 'goals', 'articles', 'circle', 'settings',
];

export const WIDGET_LABELS: Record<string, string> = {
  'checkin': 'Daily Check-In',
  'weather': 'Weather Report',
  'journeys': 'Active Journeys',
  'medications': 'Medications',
  'quick-access': 'Quick Access',
  'patterns': 'Patterns',
  'goals': 'Goals',
  'articles': 'Articles',
  'circle': 'The Circle',
  'settings': 'Settings & Preferences',
};

export function useWidgetLayout() {
  const [dashboardEditMode, setDashboardEditMode] = useState(false);

  const [widgetOrder, setWidgetOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(SK_WIDGET_ORDER);
      if (saved) {
        const parsed: string[] = JSON.parse(saved);
        const withNew = [
          ...parsed.filter(id => DEFAULT_WIDGET_ORDER.includes(id)),
          ...DEFAULT_WIDGET_ORDER.filter(id => !parsed.includes(id)),
        ];
        return withNew;
      }
    } catch { /* localStorage may be unavailable */ }
    return [...DEFAULT_WIDGET_ORDER];
  });

  const [widgetVisible, setWidgetVisible] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(SK_WIDGET_VISIBLE);
      if (saved) return { ...Object.fromEntries(DEFAULT_WIDGET_ORDER.map(id => [id, true])), ...JSON.parse(saved) };
    } catch { /* localStorage may be unavailable */ }
    return Object.fromEntries(DEFAULT_WIDGET_ORDER.map(id => [id, true]));
  });

  const [dragWidgetId, setDragWidgetId] = useState<string | null>(null);
  const [dragOverWidgetId, setDragOverWidgetId] = useState<string | null>(null);
  const touchDragIdRef = useRef<string | null>(null);
  const widgetRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const saveWidgetLayout = useCallback((order: string[], visible: Record<string, boolean>) => {
    try {
      localStorage.setItem(SK_WIDGET_ORDER, JSON.stringify(order));
      localStorage.setItem(SK_WIDGET_VISIBLE, JSON.stringify(visible));
    } catch { /* localStorage may be unavailable */ }
  }, []);

  const toggleWidgetVisible = useCallback((id: string) => {
    setWidgetVisible(prev => {
      const next = { ...prev, [id]: !prev[id] };
      setWidgetOrder(currentOrder => {
        saveWidgetLayout(currentOrder, next);
        return currentOrder;
      });
      return next;
    });
  }, [saveWidgetLayout]);

  const handleDragStart = useCallback((id: string) => {
    setDragWidgetId(id);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, id: string) => {
    e.preventDefault();
    setDragOverWidgetId(id);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    setDragWidgetId(currentDragId => {
      if (!currentDragId || currentDragId === targetId) {
        setDragOverWidgetId(null);
        return null;
      }
      setWidgetOrder(prevOrder => {
        const newOrder = [...prevOrder];
        const fromIdx = newOrder.indexOf(currentDragId);
        const toIdx = newOrder.indexOf(targetId);
        if (fromIdx === -1 || toIdx === -1) return prevOrder;
        newOrder.splice(fromIdx, 1);
        newOrder.splice(toIdx, 0, currentDragId);
        setWidgetVisible(vis => {
          saveWidgetLayout(newOrder, vis);
          return vis;
        });
        return newOrder;
      });
      setDragOverWidgetId(null);
      return null;
    });
  }, [saveWidgetLayout]);

  const handleDragEnd = useCallback(() => {
    setDragWidgetId(null);
    setDragOverWidgetId(null);
  }, []);

  const handleTouchStart = useCallback((id: string) => {
    touchDragIdRef.current = id;
    setDragWidgetId(id);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchDragIdRef.current) return;
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!el) return;
    for (const [wid, ref] of Object.entries(widgetRefs.current)) {
      if (ref && ref.contains(el) && wid !== touchDragIdRef.current) {
        setDragOverWidgetId(wid);
        return;
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    const fromId = touchDragIdRef.current;
    touchDragIdRef.current = null;
    setDragWidgetId(null);
    setDragOverWidgetId(currentDragOver => {
      if (!fromId || !currentDragOver || fromId === currentDragOver) return null;
      setWidgetOrder(prevOrder => {
        const newOrder = [...prevOrder];
        const fromIdx = newOrder.indexOf(fromId);
        const toIdx = newOrder.indexOf(currentDragOver);
        if (fromIdx === -1 || toIdx === -1) return prevOrder;
        newOrder.splice(fromIdx, 1);
        newOrder.splice(toIdx, 0, fromId);
        setWidgetVisible(vis => {
          saveWidgetLayout(newOrder, vis);
          return vis;
        });
        return newOrder;
      });
      return null;
    });
  }, [saveWidgetLayout]);

  return {
    dashboardEditMode,
    setDashboardEditMode,
    widgetOrder,
    widgetVisible,
    setWidgetVisible,
    dragWidgetId,
    dragOverWidgetId,
    widgetRefs,
    saveWidgetLayout,
    toggleWidgetVisible,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
}
